import React, { useMemo } from "react";

/**
 * Reference table showing business income breakdown.
 * Single scrolling table with visual sections: Invoices, Expenses, Summary
 * Includes year total columns between December and January
 *
 * Historical data (before cutoff): Uses businessMonthlyIncomeByClient for income,
 * moneyLineItems filtered by business for expenses.
 * Current data (cutoff and after): Uses invoice-based calculations with fixed monthly expenses.
 */
const BusinessTakehomeTable = ({
	months,
	invoicesByMonth,
	historicalIncomeByMonth = {},
	historicalExpensesByMonth = {},
	historicalCutoff = "2026-01",
	businessExpenses,
	taxRate,
	formatCurrency,
	scrollRef,
	onScroll,
	forecastItems = [],
	isCurrentMonth = () => false,
	monthProgress = 0,
	getLiveItemData = () => ({ hasData: false, actual: 0, remaining: 0 }),
	getEffectiveItemValue = (item, dateKey) => item.monthlyValues?.[dateKey] || 0,
	overdueAmount = 0,
}) => {
	// Helper to determine if a month uses historical data
	const isHistorical = (dateKey) => dateKey < historicalCutoff;

	// Get business expense items from forecast
	const businessExpenseItems = useMemo(() => {
		return forecastItems.filter(item => item.section === "Business" && !item.is_income);
	}, [forecastItems]);

	// Get invoice data for a month (returns historical or current data)
	const getMonthInvoiceData = (dateKey) => {
		if (isHistorical(dateKey)) {
			const total = historicalIncomeByMonth[dateKey] || 0;
			return {
				total,
				paid: total, // Historical data = all considered "paid"
				sent: 0,
				open: 0,
			};
		}
		return invoicesByMonth[dateKey] || { total: 0, paid: 0, sent: 0, open: 0 };
	};

	// Get expenses for a month - uses forecast items with proration and large-ticket handling
	const getMonthExpenses = (dateKey) => {
		if (isHistorical(dateKey)) {
			return historicalExpensesByMonth[dateKey] || 0;
		}

		// If no forecast items, fall back to fixed businessExpenses
		if (businessExpenseItems.length === 0) {
			return businessExpenses;
		}

		// Calculate from forecast items with large-ticket handling
		let total = businessExpenseItems.reduce((sum, item) => {
			const forecastValue = getEffectiveItemValue(item, dateKey);
			if (item.is_large_ticket) {
				const liveData = getLiveItemData(item.id, dateKey, forecastValue);
				// Use remaining amount if we have live data
				return sum + (liveData.hasData ? (forecastValue >= 0 ? liveData.remaining : -liveData.remaining) : forecastValue);
			}
			return sum + forecastValue;
		}, 0);

		// Prorate for current month (only remaining portion)
		if (isCurrentMonth(dateKey)) {
			total = total * (1 - monthProgress);
		}

		// Business expenses are stored as negative, return absolute value
		return Math.abs(total);
	};
	// Build columns with year totals inserted after each year
	const columns = useMemo(() => {
		const cols = [];
		let currentYear = null;
		let yearMonths = [];

		months.forEach((m, i) => {
			const year = m.dateKey.split("-")[0];

			// If year changed, insert year total for previous year
			if (currentYear && year !== currentYear) {
				cols.push({
					type: "year-total",
					year: currentYear,
					months: [...yearMonths],
				});
				yearMonths = [];
			}

			currentYear = year;
			yearMonths.push(m);
			cols.push({
				type: "month",
				month: m,
				index: i,
			});
		});

		// Add final year total
		if (yearMonths.length > 0) {
			cols.push({
				type: "year-total",
				year: currentYear,
				months: yearMonths,
			});
		}

		return cols;
	}, [months]);

	// Get effective total for a month (includes overdue for current month)
	const getEffectiveTotal = (dateKey) => {
		const invoiceData = getMonthInvoiceData(dateKey);
		return invoiceData.total + (isCurrentMonth(dateKey) ? overdueAmount : 0);
	};

	// Calculate year totals for invoices
	const getYearInvoiceTotals = (yearMonths, field) => {
		return yearMonths.reduce((sum, m) => sum + (getMonthInvoiceData(m.dateKey)[field] || 0), 0);
	};

	// Calculate year expenses
	const getYearExpenses = (yearMonths) => {
		return yearMonths.reduce((sum, m) => sum + getMonthExpenses(m.dateKey), 0);
	};

	// Calculate year net (includes overdue for current month)
	const getYearNet = (yearMonths) => {
		return yearMonths.reduce((sum, m) => {
			return sum + getEffectiveTotal(m.dateKey) - getMonthExpenses(m.dateKey);
		}, 0);
	};

	// Calculate year taxes (includes overdue for current month)
	const getYearTaxes = (yearMonths) => {
		return yearMonths.reduce((sum, m) => {
			const total = getEffectiveTotal(m.dateKey);
			const expenses = getMonthExpenses(m.dateKey);
			const net = total - expenses;
			return sum + (net > 0 ? net * taxRate : 0);
		}, 0);
	};

	// Calculate year takehome (includes overdue for current month)
	const getYearTakehome = (yearMonths) => {
		return yearMonths.reduce((sum, m) => {
			const total = getEffectiveTotal(m.dateKey);
			const expenses = getMonthExpenses(m.dateKey);
			const net = total - expenses;
			const taxes = net > 0 ? net * taxRate : 0;
			return sum + (net - taxes);
		}, 0);
	};

	return (
		<div className="budget-forecast__section">
			<h2 className="budget-forecast__section-title">Business Takehome Income</h2>

			<div className="budget-forecast__table-wrapper" ref={scrollRef} onScroll={onScroll}>
				<table className="budget-forecast__table budget-forecast__table--takehome">
					<thead>
						<tr>
							<th className="budget-forecast__th-name"></th>
							{columns.map((col, i) => (
								col.type === "month" ? (
									<th key={col.month.dateKey} className={`budget-forecast__th-month ${col.index % 2 === 1 ? "budget-forecast__th-month--alt" : ""}`}>
										{col.month.label}
									</th>
								) : (
									<th key={`year-${col.year}`} className="budget-forecast__th-month budget-forecast__th-year">
										{col.year}
									</th>
								)
							))}
						</tr>
					</thead>
					<tbody>
						{/* Invoice Totals Section */}
						<tr className="budget-forecast__row budget-forecast__row--section-label">
							<td className="budget-forecast__td-name">Invoices</td>
							{columns.map((col) => (
								<td key={col.type === "month" ? col.month.dateKey : `year-${col.year}`} className={`budget-forecast__td-value ${col.type === "month" && col.index % 2 === 1 ? "budget-forecast__td-value--alt" : ""} ${col.type === "year-total" ? "budget-forecast__td-value--year" : ""}`}></td>
							))}
						</tr>
						<tr className="budget-forecast__row budget-forecast__row--total">
							<td className="budget-forecast__td-name">Total</td>
							{columns.map((col) => (
								col.type === "month" ? (
									<td key={col.month.dateKey} className={`budget-forecast__td-value budget-forecast__td-value--income ${col.index % 2 === 1 ? "budget-forecast__td-value--alt" : ""} ${isHistorical(col.month.dateKey) ? "budget-forecast__td-value--historical" : ""}`}>
										{formatCurrency(getMonthInvoiceData(col.month.dateKey).total)}
									</td>
								) : (
									<td key={`year-${col.year}`} className="budget-forecast__td-value budget-forecast__td-value--income budget-forecast__td-value--year">
										{formatCurrency(getYearInvoiceTotals(col.months, "total"))}
									</td>
								)
							))}
						</tr>
						<tr className="budget-forecast__row budget-forecast__row--indent">
							<td className="budget-forecast__td-name">Paid</td>
							{columns.map((col) => (
								col.type === "month" ? (
									<td key={col.month.dateKey} className={`budget-forecast__td-value budget-forecast__td-value--positive ${col.index % 2 === 1 ? "budget-forecast__td-value--alt" : ""} ${isHistorical(col.month.dateKey) ? "budget-forecast__td-value--historical" : ""}`}>
										{formatCurrency(getMonthInvoiceData(col.month.dateKey).paid)}
									</td>
								) : (
									<td key={`year-${col.year}`} className="budget-forecast__td-value budget-forecast__td-value--positive budget-forecast__td-value--year">
										{formatCurrency(getYearInvoiceTotals(col.months, "paid"))}
									</td>
								)
							))}
						</tr>
						<tr className="budget-forecast__row budget-forecast__row--indent">
							<td className="budget-forecast__td-name">Sent</td>
							{columns.map((col) => (
								col.type === "month" ? (
									<td key={col.month.dateKey} className={`budget-forecast__td-value ${col.index % 2 === 1 ? "budget-forecast__td-value--alt" : ""} ${isHistorical(col.month.dateKey) ? "budget-forecast__td-value--historical" : ""}`}>
										{formatCurrency(getMonthInvoiceData(col.month.dateKey).sent)}
									</td>
								) : (
									<td key={`year-${col.year}`} className="budget-forecast__td-value budget-forecast__td-value--year">
										{formatCurrency(getYearInvoiceTotals(col.months, "sent"))}
									</td>
								)
							))}
						</tr>
						<tr className="budget-forecast__row budget-forecast__row--indent">
							<td className="budget-forecast__td-name">Open</td>
							{columns.map((col) => (
								col.type === "month" ? (
									<td key={col.month.dateKey} className={`budget-forecast__td-value ${col.index % 2 === 1 ? "budget-forecast__td-value--alt" : ""} ${isHistorical(col.month.dateKey) ? "budget-forecast__td-value--historical" : ""}`}>
										{formatCurrency(getMonthInvoiceData(col.month.dateKey).open)}
									</td>
								) : (
									<td key={`year-${col.year}`} className="budget-forecast__td-value budget-forecast__td-value--year">
										{formatCurrency(getYearInvoiceTotals(col.months, "open"))}
									</td>
								)
							))}
						</tr>
						{overdueAmount > 0 && (
							<tr className="budget-forecast__row budget-forecast__row--indent budget-forecast__row--overdue">
								<td className="budget-forecast__td-name">Overdue</td>
								{columns.map((col) => {
									if (col.type === "month") {
										const value = isCurrentMonth(col.month.dateKey) ? overdueAmount : 0;
										return (
											<td key={col.month.dateKey} className={`budget-forecast__td-value ${value > 0 ? "budget-forecast__td-value--overdue" : ""} ${col.index % 2 === 1 ? "budget-forecast__td-value--alt" : ""}`}>
												{formatCurrency(value)}
											</td>
										);
									} else {
										// Year total: include overdue if current month falls in this year
										const yearHasCurrent = col.months.some((m) => isCurrentMonth(m.dateKey));
										return (
											<td key={`year-${col.year}`} className={`budget-forecast__td-value budget-forecast__td-value--year ${yearHasCurrent ? "budget-forecast__td-value--overdue" : ""}`}>
												{formatCurrency(yearHasCurrent ? overdueAmount : 0)}
											</td>
										);
									}
								})}
							</tr>
						)}

						{/* Expenses Section */}
						<tr className="budget-forecast__row budget-forecast__row--section-label budget-forecast__row--section-spacer">
							<td className="budget-forecast__td-name">Expenses</td>
							{columns.map((col) => (
								<td key={col.type === "month" ? col.month.dateKey : `year-${col.year}`} className={`budget-forecast__td-value ${col.type === "month" && col.index % 2 === 1 ? "budget-forecast__td-value--alt" : ""} ${col.type === "year-total" ? "budget-forecast__td-value--year" : ""}`}></td>
							))}
						</tr>
						<tr className="budget-forecast__row">
							<td className="budget-forecast__td-name">Monthly</td>
							{columns.map((col) => (
								col.type === "month" ? (
									<td key={col.month.dateKey} className={`budget-forecast__td-value budget-forecast__td-value--expense ${col.index % 2 === 1 ? "budget-forecast__td-value--alt" : ""} ${isHistorical(col.month.dateKey) ? "budget-forecast__td-value--historical" : ""}`}>
										{formatCurrency(-getMonthExpenses(col.month.dateKey))}
									</td>
								) : (
									<td key={`year-${col.year}`} className="budget-forecast__td-value budget-forecast__td-value--expense budget-forecast__td-value--year">
										{formatCurrency(-getYearExpenses(col.months))}
									</td>
								)
							))}
						</tr>

						{/* Summary Section */}
						<tr className="budget-forecast__row budget-forecast__row--section-label budget-forecast__row--section-spacer">
							<td className="budget-forecast__td-name">Summary</td>
							{columns.map((col) => (
								<td key={col.type === "month" ? col.month.dateKey : `year-${col.year}`} className={`budget-forecast__td-value ${col.type === "month" && col.index % 2 === 1 ? "budget-forecast__td-value--alt" : ""} ${col.type === "year-total" ? "budget-forecast__td-value--year" : ""}`}></td>
							))}
						</tr>
						<tr className="budget-forecast__row">
							<td className="budget-forecast__td-name">Net</td>
							{columns.map((col) => {
								if (col.type === "month") {
									const total = getEffectiveTotal(col.month.dateKey);
									const expenses = getMonthExpenses(col.month.dateKey);
									const net = total - expenses;
									return (
										<td key={col.month.dateKey} className={`budget-forecast__td-value ${net >= 0 ? "budget-forecast__td-value--positive" : "budget-forecast__td-value--negative"} ${col.index % 2 === 1 ? "budget-forecast__td-value--alt" : ""} ${isHistorical(col.month.dateKey) ? "budget-forecast__td-value--historical" : ""}`}>
											{formatCurrency(net)}
										</td>
									);
								} else {
									const yearNet = getYearNet(col.months);
									return (
										<td key={`year-${col.year}`} className={`budget-forecast__td-value budget-forecast__td-value--year ${yearNet >= 0 ? "budget-forecast__td-value--positive" : "budget-forecast__td-value--negative"}`}>
											{formatCurrency(yearNet)}
										</td>
									);
								}
							})}
						</tr>
						<tr className="budget-forecast__row">
							<td className="budget-forecast__td-name">Taxes ({Math.round(taxRate * 100)}%)</td>
							{columns.map((col) => {
								if (col.type === "month") {
									const total = getEffectiveTotal(col.month.dateKey);
									const expenses = getMonthExpenses(col.month.dateKey);
									const net = total - expenses;
									const taxes = net > 0 ? net * taxRate : 0;
									return (
										<td key={col.month.dateKey} className={`budget-forecast__td-value budget-forecast__td-value--expense ${col.index % 2 === 1 ? "budget-forecast__td-value--alt" : ""} ${isHistorical(col.month.dateKey) ? "budget-forecast__td-value--historical" : ""}`}>
											{formatCurrency(-taxes)}
										</td>
									);
								} else {
									return (
										<td key={`year-${col.year}`} className="budget-forecast__td-value budget-forecast__td-value--expense budget-forecast__td-value--year">
											{formatCurrency(-getYearTaxes(col.months))}
										</td>
									);
								}
							})}
						</tr>
						<tr className="budget-forecast__row budget-forecast__row--takehome">
							<td className="budget-forecast__td-name">Takehome</td>
							{columns.map((col) => {
								if (col.type === "month") {
									const total = getEffectiveTotal(col.month.dateKey);
									const expenses = getMonthExpenses(col.month.dateKey);
									const net = total - expenses;
									const taxes = net > 0 ? net * taxRate : 0;
									const takehome = net - taxes;
									return (
										<td key={col.month.dateKey} className={`budget-forecast__td-value budget-forecast__td-value--large ${takehome >= 0 ? "budget-forecast__td-value--positive" : "budget-forecast__td-value--negative"} ${col.index % 2 === 1 ? "budget-forecast__td-value--alt" : ""} ${isHistorical(col.month.dateKey) ? "budget-forecast__td-value--historical" : ""}`}>
											{formatCurrency(takehome)}
										</td>
									);
								} else {
									const yearTakehome = getYearTakehome(col.months);
									return (
										<td key={`year-${col.year}`} className={`budget-forecast__td-value budget-forecast__td-value--large budget-forecast__td-value--year ${yearTakehome >= 0 ? "budget-forecast__td-value--positive" : "budget-forecast__td-value--negative"}`}>
											{formatCurrency(yearTakehome)}
										</td>
									);
								}
							})}
						</tr>
						{/* Pending Takehome - for troubleshooting: (sent + open + overdue - expenses) after taxes */}
						<tr className="budget-forecast__row budget-forecast__row--indent">
							<td className="budget-forecast__td-name">Pending Takehome</td>
							{columns.map((col) => {
								if (col.type === "month") {
									const invoiceData = getMonthInvoiceData(col.month.dateKey);
									const expenses = getMonthExpenses(col.month.dateKey);
									const overdue = isCurrentMonth(col.month.dateKey) ? overdueAmount : 0;
									const pendingGross = invoiceData.total - invoiceData.paid + overdue; // sent + open + overdue
									const pendingNet = pendingGross - expenses;
									const pendingTaxes = pendingNet > 0 ? pendingNet * taxRate : 0;
									const pendingTakehome = pendingNet - pendingTaxes;
									return (
										<td key={col.month.dateKey} className={`budget-forecast__td-value ${pendingTakehome >= 0 ? "budget-forecast__td-value--pending" : "budget-forecast__td-value--negative"} ${col.index % 2 === 1 ? "budget-forecast__td-value--alt" : ""}`}
											title={`Gross: ${formatCurrency(pendingGross)}${overdue > 0 ? ` (incl. ${formatCurrency(overdue)} overdue)` : ""} | Net: ${formatCurrency(pendingNet)} | Tax: ${formatCurrency(pendingTaxes)}`}>
											{formatCurrency(pendingTakehome)}
										</td>
									);
								} else {
									const yearPendingTakehome = col.months.reduce((sum, m) => {
										const invoiceData = getMonthInvoiceData(m.dateKey);
										const expenses = getMonthExpenses(m.dateKey);
										const overdue = isCurrentMonth(m.dateKey) ? overdueAmount : 0;
										const pendingGross = invoiceData.total - invoiceData.paid + overdue;
										const pendingNet = pendingGross - expenses;
										const pendingTaxes = pendingNet > 0 ? pendingNet * taxRate : 0;
										return sum + (pendingNet - pendingTaxes);
									}, 0);
									return (
										<td key={`year-${col.year}`} className={`budget-forecast__td-value budget-forecast__td-value--year ${yearPendingTakehome >= 0 ? "budget-forecast__td-value--pending" : "budget-forecast__td-value--negative"}`}>
											{formatCurrency(yearPendingTakehome)}
										</td>
									);
								}
							})}
						</tr>
					</tbody>
				</table>
			</div>
		</div>
	);
};

export default BusinessTakehomeTable;
